import React, { useEffect, useState } from 'react';
import { Button, Container, IconButton, Link, Stack } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { Field, Form } from 'react-final-form';
import {
  InfoOutlined,
  LaptopMacOutlined,
  Person2Outlined,
  Telegram,
  VerifiedUserOutlined,
  WhatsApp,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  composeValidators,
  parseNumber,
  validateRequired,
  validateTel,
} from '../helpers/ValidatorHelper';
import NumberFormatTelAdapter from '../components/NumberFormat/NumberFormatTelAdapter';
import TextFieldAdapter from '../components/FinalFormAdapters/TextFieldAdapter';
import { signUp } from '../api/auth';
import useClosableSnackbar from '../hooks/useClosableSnackbar';
import SmsSender from '../components/SmsSender';
import { CodeValidatedActionEnum } from '../enum/CodeValidatedActionEnum';
import styles from './SignUp.module.scss';
import CheckBoxAdapter from '../components/FinalFormAdapters/CheckBoxAdapter';
import SignUpTimeLine from '../components/SignUpTimeLine/SignUpTimeLine';
import ProfitCalculator from '../components/ProfitCalculator/ProfitCalculator';
import ResponsiveAppBar from '../layout/ResponsiveAppBar';
import Footer from '../layout/Footer';
import {
  errorToString,
  reachGoalYandex,
  toAbsoluteUrl,
} from '../helpers/Helper';
import useCheckAuth from '../hooks/useCheckAuth';
import useAgentSystemDocuments from '../hooks/useAgentSystemDocuments';

interface SignUpFormValues {
  phone: string;
  code: string;
}

const SignUp = () => {
  const [processing, setProcessing] = useState<boolean>(false);
  const { showSnackbar } = useClosableSnackbar();
  const navigate = useNavigate();
  const checkAuth = useCheckAuth();
  const { getByType } = useAgentSystemDocuments();
  const commonConditionsSD = getByType('AGENT_COMMON_CONDITIONS');

  useEffect(() => {
    checkAuth();
  }, []);

  const submit = async ({ code, phone }: SignUpFormValues) => {
    setProcessing(true);
    try {
      const r = await signUp(`7${parseNumber(phone)}`, code);
      if (r && r.accessToken) {
        reachGoalYandex('agentregisterend');
        localStorage.setItem('user', JSON.stringify(r));
        navigate('/');
      } else {
        showSnackbar('no accounts');
        console.log('no accounts'); // todo: remove this line
      }
    } catch (e) {
      showSnackbar(errorToString(e));
      console.log(e); // todo: remove this line
    }
    setProcessing(false);
  };

  return (
    <>
      <ResponsiveAppBar title="Регистрация в агентском кабинете" />
      <Container maxWidth="lg" style={{ textAlign: 'center' }}>
        <div className={styles.headerWrapper}>
          <h2 className={styles.signUpH2}>
            Зарабатывайте на привлечении заёмщиков на платформу Money Friends
          </h2>
        </div>
        <Grid2 container spacing={2} justifyContent="center">
          <Grid2
            xs={12}
            style={{ maxWidth: '500px' }}
            // sx={{ mt: { xs: 2, sm: 3 } }}
          >
            <Form
              onSubmit={submit}
              render={({ handleSubmit, values }) => (
                <form onSubmit={handleSubmit}>
                  <Field
                    variant="outlined"
                    type="tel"
                    required
                    label="Телефон"
                    fullWidth
                    margin="normal"
                    name="phone"
                    validate={composeValidators(validateTel, validateRequired)}
                    component={NumberFormatTelAdapter}
                    maxLength={10}
                  />
                  <div style={{ marginTop: '1rem' }}>
                    <Field
                      name="accept"
                      component={CheckBoxAdapter}
                      type="checkbox"
                      label={
                        <span
                          style={{
                            textAlign: 'left',
                            fontSize: '13px',
                            lineHeight: 1.4,
                            display: 'inline-block',
                            verticalAlign: 'middle',
                          }}
                        >
                          Ознакомлен с{' '}
                          <Link
                            href={toAbsoluteUrl('/v1/document-privacy-policy')}
                            target="_blank"
                          >
                            политикой конфиденциальности
                          </Link>{' '}
                          и подтверждаю{' '}
                          <Link
                            href={toAbsoluteUrl(
                              '/v1/document-privacy-agreement'
                            )}
                            target="_blank"
                          >
                            согласие на обработку персональных данных
                          </Link>
                          ,{' '}
                          <Link
                            href={toAbsoluteUrl(
                              '/v1/document-privacy-distribution-agreement'
                            )}
                            target="_blank"
                          >
                            согласие на обработку персональных данных,
                            разрешенных для распространения
                          </Link>
                        </span>
                      }
                      labelClassName="signup__checkbox-label"
                      color="primary"
                      validate={validateRequired}
                    />
                  </div>
                  {Boolean(values.phone) && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <SmsSender
                        format="(###) ### ## ##"
                        prefix="+7 "
                        phone={parseNumber(values.phone)}
                        action={CodeValidatedActionEnum.SIGN_UP}
                      />
                      <Field
                        sx={{ mt: 3 }}
                        variant="outlined"
                        name="code"
                        required
                        label="Код подтверждения"
                        margin="none"
                        parse={(value: string) =>
                          value.length > 6 ? value.substr(0, 6) : value
                        }
                        component={TextFieldAdapter}
                        validate={validateRequired}
                        fullWidth
                      />
                    </div>
                  )}
                  <Button
                    sx={{ mt: 3 }}
                    size="large"
                    disabled={processing}
                    type="submit"
                    variant="contained"
                  >
                    Стать агентом
                  </Button>
                  <Stack style={{ marginTop: '1rem' }}>
                    <Link to="/signIn" component={RouterLink}>
                      Я уже зарегистрирован
                    </Link>
                    <Link href="/lk/index.html#/signUp/borrower">
                      Зарегистрироваться как заёмщик
                    </Link>
                    <Link href="/lk/index.html#/signUp/investor">
                      Зарегистрироваться как инвестор
                    </Link>
                  </Stack>
                </form>
              )}
            />
          </Grid2>
          <div className={styles.utpWrapper}>
            <div className={styles.utpItem}>
              <VerifiedUserOutlined className={styles.utpIcon} />
              <div>Выплата от 10 000 руб за 1 клиента</div>
            </div>
            <div className={styles.utpItem}>
              <Person2Outlined className={styles.utpIcon} />
              <div>
                Удобный личный кабинет для работы и отслеживание результатов со
                статистикой
              </div>
            </div>
            <div className={styles.utpItem}>
              <InfoOutlined className={styles.utpIcon} />
              <div>Информационная поддержка</div>
            </div>
            <div className={styles.utpItem}>
              <LaptopMacOutlined className={styles.utpIcon} />
              <div>Договор онлайн</div>
            </div>
          </div>
          <h2 className={styles.signUpH2} style={{ marginTop: '90px' }}>
            Как работает агентская программа?
          </h2>
        </Grid2>
        <SignUpTimeLine />
        <h2 className={styles.signUpH2} style={{ marginTop: '90px' }}>
          Сколько я могу зарабатывать?
        </h2>
        <ProfitCalculator />
        <h2 className={styles.signUpH2} style={{ marginTop: '90px' }}>
          На каких условиях работает агентская программа?
        </h2>
        <div>
          Ознакомиться с условиями проведения агентской программы вы можете{' '}
          <Link target="_blank" href={commonConditionsSD?.viewUrl}>
            тут
          </Link>
        </div>
        <h2 className={styles.signUpH2} style={{ marginTop: '90px' }}>
          Остались вопросы? Свяжитесь с нами
        </h2>
        <div>
          <div>
            <IconButton
              href="https://api.whatsapp.com/send/?phone=79064140198&text&type=phone_number&app_absent=0"
              target="_blank"
              sx={{ m: 3 }}
            >
              <WhatsApp
                className={styles.contactIcon}
                style={{ fill: '#1FAF38' }}
              />
            </IconButton>
            <IconButton
              href="https://t.me/moneyfriends_ru"
              target="_blank"
              sx={{ m: 3 }}
            >
              <Telegram
                className={styles.contactIcon}
                style={{ fill: '#2AABEE' }}
              />
            </IconButton>
          </div>
          <div style={{ fontSize: '24px' }}>
            Телефон: <Link href="tel:88005504264">8-800-550-42-64</Link>
          </div>
          <div style={{ fontSize: '24px' }}>
            E-mail:{' '}
            <Link href="mailto:hello@moneyfriends.ru">
              hello@moneyfriends.ru
            </Link>
          </div>
        </div>
      </Container>
      <Footer />
    </>
  );
};

export default SignUp;
